import { Button, Typography } from '@mui/material';
import { ConfirmDialog, Page } from 'cleveron-web-components';
import { usePreviousCompare } from 'cleveron-web-components/hooks';
import { pick } from 'lodash';
import queryString from 'query-string';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { bindActionCreators } from 'redux';

import { handleSubmitFail } from '../form';
import usePathMatch from '../utils/usePathMatch';
import usePermissions from '../utils/usePermissions';
import ErrorPage from './ErrorPage';
import Error403 from './ErrorPage/Error403';
import LoadingComponent from './LoadingComponent';

function ItemPage(props) {
    const {
        Actions,
        addItem,
        canCreate,
        canDelete,
        canUpdate,
        Child,
        deleteButtonProps,
        deleteButtonTranslationKey,
        deleteItem,
        errorPageOnError,
        fetchItem,
        hideDelete,
        hideSave = false,
        id,
        isNew,
        ItemForm,
        items,
        preItems,
        refetchItem,
        refetchAfterUpdate,
        saveButtonProps,
        saveButtonTranslationKey,
        tabs,
        updateItem,
    } = props;
    const { url } = useRouteMatch();
    const history = useHistory();
    const { itemId, viewId = '', ...otherUrlParams } = useParams();
    const { collectionName, collectionPath, translationKey } = usePathMatch();
    const [deleteModalOpened, setDeleteModalOpened] = useState(false);

    let item;
    const numId = parseInt(itemId, 10);
    if (Number.isNaN(numId)) {
        item = isNew ? null : items[itemId];
    } else {
        item = items[numId];
    }

    const error = item && item.error;

    if (error && errorPageOnError) {
        return <ErrorPage error={error} id={id} />;
    }

    const handleSubmitSuccess = ({ id: newItemId, updatedId }) => {
        if (typeof newItemId === 'number') {
            let pathname = url;
            if (collectionName === 'apms') {
                pathname = `${collectionPath}/${newItemId}/edit`;
            }
            if (isNew) {
                pathname = url.replace('new', newItemId);
            }
            history.replace({ pathname });
        } else if (typeof updatedId === 'number') {
            const pathname = `${collectionPath}/${updatedId}`;
            history.replace({ pathname });
        } else if (refetchAfterUpdate) {
            refetchItem();
        }
    };

    const actions = (
        <>
            {Actions && <Actions {...props} id={id} />}
            {!isNew && canDelete && !hideDelete && (
                <>
                    <Button
                        color="black"
                        id={`${id}DeleteButton`}
                        name="delete"
                        onClick={() => setDeleteModalOpened(true)}
                        type="button"
                        {...deleteButtonProps}
                    >
                        <FormattedMessage
                            id={
                                deleteButtonTranslationKey ||
                                defineMessage({
                                    defaultMessage: 'Delete',
                                    id: 'default.delete',
                                }).id
                            }
                        />
                    </Button>
                    <ConfirmDialog
                        confirmKey={
                            defineMessage({
                                defaultMessage: 'Delete',
                                id: 'default.delete.confirm',
                            }).id
                        }
                        id={`${id}DeleteConfirmation`}
                        onClose={() => setDeleteModalOpened(false)}
                        onConfirm={async () => {
                            setDeleteModalOpened(false);
                            const res = await deleteItem({ id: itemId, ...otherUrlParams });
                            if (!(res instanceof Error)) {
                                history.replace({ pathname: collectionPath });
                            }
                        }}
                        open={deleteModalOpened}
                        titleKey={
                            defineMessage({
                                defaultMessage: 'Are you sure you want to delete this item?',
                                id: 'default.delete.question',
                            }).id
                        }
                    >
                        <Typography>
                            <FormattedMessage
                                defaultMessage="This item will be deleted permanently"
                                id="default.delete.itemWillBeDeleted"
                            />
                        </Typography>
                    </ConfirmDialog>
                </>
            )}
        </>
    );

    const enabled = (isNew && canCreate) || (!isNew && canUpdate);
    return (
        <Page
            id={`${id}Page`}
            titleKey={isNew ? `${translationKey}.new` : `${translationKey}.item`}
        >
            <ItemForm
                ItemFormProps={{
                    actions,
                    disabled: !enabled,
                    hideSave,
                    isNew,
                    SaveButtonProps: saveButtonProps,
                    saveButtonTranslationKey,
                    tabs,
                    viewId,
                }}
                back={!!window.BACK_BUTTON}
                canCreate={canCreate}
                canDelete={canDelete}
                canUpdate={canUpdate}
                disabled={!enabled}
                fetchItem={fetchItem}
                id={translationKey}
                isNew={isNew}
                item={item}
                keepDirtyOnReinitialize
                onSubmit={isNew ? addItem : updateItem}
                onSubmitFail={handleSubmitFail}
                onSubmitSuccess={handleSubmitSuccess}
                preItems={preItems}
                {...otherUrlParams}
            />
            {Child && <Child id={id} {...props} />}
        </Page>
    );
}

export default function createItemHOC({
    Actions,
    actions,
    Child,
    errorPageOnError = true,
    fetchParamsKeys = [],
    fetchPreActions = {},
    fetchPreActionsProps = {},
    includeLocale = false,
    ItemForm,
    mapStateToProps,
    permissions,
    refetchAfterUpdate,
}) {
    const ConnectedItemPage = connect(mapStateToProps)(ItemPage);

    return connect(
        state => ({ locale: state.locale }),
        dispatch => bindActionCreators({ ...actions, ...fetchPreActions }, dispatch)
    )(props => {
        const { fetchItem, id, locale, tabs } = props;
        const { create, delete: canDelete, read, update } = usePermissions(permissions);
        const { translationKey } = usePathMatch();
        const [loading, setLoading] = useState(true);
        const { itemId, ...otherUrlParams } = useParams();
        const location = useLocation();
        const search = usePreviousCompare(queryString.parse(location.search));
        const fetchUrlParams = usePreviousCompare(pick(otherUrlParams, fetchParamsKeys));
        const isNew = !(itemId && itemId !== 'new');

        const callFetchItem = useCallback(
            () =>
                fetchItem({
                    id: itemId,
                    ...fetchUrlParams,
                    ...(includeLocale && { locale }),
                }),
            [fetchItem, fetchUrlParams, itemId, locale]
        );

        useEffect(() => {
            let isCancelled = false;
            if (read) {
                (async function fetchData() {
                    await Promise.all(
                        Object.keys(fetchPreActions).map(key => {
                            let extraProps = fetchPreActionsProps[key];

                            if (typeof extraProps === 'function') {
                                extraProps = extraProps({
                                    id: itemId,
                                    params: fetchUrlParams,
                                    search,
                                });
                            }

                            if (extraProps === null) {
                                return null;
                            }

                            return props[key]({ ...extraProps });
                        })
                    );
                    if (itemId != null && !isNew) {
                        setLoading(true);
                        await callFetchItem();
                    }
                    if (!isCancelled) {
                        setLoading(false);
                    }
                })();
            } else {
                setLoading(false);
            }

            return () => {
                isCancelled = true;
            };
        }, [callFetchItem, fetchUrlParams, isNew, itemId, props, read, search]);

        if (loading) {
            return (
                <LoadingComponent
                    hasBackButton={!!window.BACK_BUTTON}
                    id={`${id}Loading`}
                    tabs={tabs}
                    titleKey={isNew ? `${translationKey}.new` : `${translationKey}.item`}
                />
            );
        }

        if (!read) {
            return <Error403 id={`${id}Error403`} />;
        }

        return (
            <ConnectedItemPage
                Actions={Actions}
                Child={Child}
                ItemForm={ItemForm}
                canCreate={create}
                canDelete={canDelete}
                canUpdate={update}
                errorPageOnError={errorPageOnError}
                isNew={isNew}
                params={fetchUrlParams}
                refetchAfterUpdate={refetchAfterUpdate}
                refetchItem={callFetchItem}
                search={search}
                {...props}
            />
        );
    });
}
